import { notification } from "antd"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { getInvoicingSettings, hideApprovedApplication, updateDefaultIncludePaymentSummary, updateDefaultLineItemGrouping, updateGeneralSettings, updateInvoicingSystemSettings } from "./invoicingSettingsApi"
import { parseQboAccountFieldErrors, QboAccountFieldErrors } from "./invoicingSettings"

export const useInvoicingSettingsQuery = () => {
  return useQuery(['invoicing-settings'], {
    queryFn: getInvoicingSettings,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    onError() {
      notification.error({ message: 'There was a problem loading invoicing settings.' })
    }
  })
}

export const useSynkedUPPaymentsStatus = () => {
  const { data, isLoading, isError } = useInvoicingSettingsQuery()
  return {
    isTenantOnSynkedUPPayments: data?.invoicingSystem === 'synkedUp',
    isKnown: data !== undefined,
    isLoading,
    isError,
  }
}

export const useHideApprovedApplicationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: hideApprovedApplication,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing-settings'])
    },
    onError: () => {
      notification.error({ message: 'There was a problem hiding the approved application.' })
    }
  })
}

type UpdateInvoicingSystemSettingsOptions = {
  onFieldErrors?: (fieldErrors: QboAccountFieldErrors) => void
}

export const useUpdateInvoicingSystemSettingsMutation = (options?: UpdateInvoicingSystemSettingsOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateInvoicingSystemSettings,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing-settings'])
    },
    onError: (error: any) => {
      const errors: unknown = error?.response?.data?.errors
      if (Array.isArray(errors)) {
        const fieldErrors = parseQboAccountFieldErrors(errors as string[])
        if (Object.keys(fieldErrors).length > 0) {
          options?.onFieldErrors?.(fieldErrors)
          return
        }
      }
      notification.error({ message: 'There was a problem updating your Invoicing System settings.' })
    }
  })
}

export const useUpdateDefaultLineItemGroupingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDefaultLineItemGrouping,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing-settings'])
    },
    onError: () => {
      notification.error({ message: 'There was a problem updating your Job Type Settings.' })
    }
  })
}

export const useUpdateGeneralSettingsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGeneralSettings,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing-settings'])
    },
    onError: () => {
      notification.error({ message: 'There was a problem updating your General Invoicing Settings.' })
    }
  })
}

export const useUpdateDefaultIncludePaymentSummaryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDefaultIncludePaymentSummary,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing-settings'])
    },
    onError: () => {
      notification.error({ message: 'There was a problem updating your Payment Summary setting.' })
    }
  })
}
